import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Typography } from '../../../../components/common/Typography/Typography';
import { GameId } from '../../../../constants/enums';
import { APP_ROUTES } from '../../../../constants/routes';
import { useActiveSection } from '../../../../hooks/useActiveSection/useActiveSection';
import { useDocumentHead } from '../../../../hooks/useDocumentHead/useDocumentHead';
import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { useGetGameByIdQuery } from '../../api/gamesApi';
import { GameHero } from '../../components/GameHero/GameHero';
import { GameMediaGallery } from '../../components/GameMediaGallery/GameMediaGallery';
import { GamePillars } from '../../components/GamePillars/GamePillars';
import { GameRoadmap } from '../../components/GameRoadmap/GameRoadmap';
import { resolveGameTitle } from '../../utils/gameUtils';
import styles from './GameProfileScreen.module.css';
import { GameProfileScreenProps } from './GameProfileScreen.types';

// The ocean backdrop pulls in pixi.js (a large WebGL engine) and only one game uses it, so it is
// split out of the main bundle and loaded on demand.
const OceanBackdrop = lazy(() =>
  import('../../components/OceanBackdrop/OceanBackdrop').then((module) => ({
    default: module.OceanBackdrop,
  }))
);

export const GameProfileScreen: React.FC<GameProfileScreenProps> = ({ id: propId }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const gameId = propId || paramId || GameId.SAIL_TO_UNKNOWN;
  const { t } = useTranslation();

  const { data: game, isLoading, error } = useGetGameByIdQuery(gameId);
  const gameTitle = game?.title || resolveGameTitle(gameId);

  useDocumentHead({
    title: gameTitle ? `${gameTitle} - ${t.brand} Games` : undefined,
    description: game ? t.seo.gameProfileDescription({ title: game.title }) : undefined,
  });

  const sectionTabs = useMemo(
    () => [
      { id: 'overview-section', label: t.games.tabs.overview },
      { id: 'features-section', label: t.games.tabs.features },
      { id: 'media-section', label: t.games.tabs.media },
      { id: 'roadmap-section', label: t.games.tabs.roadmap },
    ],
    [t]
  );
  const sectionIds = useMemo(() => sectionTabs.map(({ id }) => id), [sectionTabs]);

  const activeSectionId = useActiveSection(sectionIds, { offset: 140 });
  const [userSelectedNavId, setUserSelectedNavId] = useState<string | null>(null);

  const activeNavId = userSelectedNavId || activeSectionId || 'overview-section';

  useEffect(() => {
    if (!userSelectedNavId) return;
    const handleScroll = () => {
      setUserSelectedNavId(null);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [userSelectedNavId]);

  const handleTabClick = (sectionId: string) => {
    setUserSelectedNavId(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper} role="status" aria-live="polite">
        <div className={styles.spinner} aria-hidden="true" />
        <div className={styles.loadingContent}>
          <Typography variant="h2" className={styles.loadingTitle}>
            {t.games.loading({ title: gameTitle })}
          </Typography>
          <Typography variant="body2" className={styles.loadingSubtitle}>
            {t.games.loadingSubtitle}
          </Typography>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className={styles.errorWrapper}>
        <Typography variant="h2">{t.games.gameNotFound}</Typography>
        <Link to={APP_ROUTES.HOME} className={styles.backLink}>
          {t.games.backToHome}
        </Link>
      </div>
    );
  }

  const isSailGame = game.id === GameId.SAIL_TO_UNKNOWN;

  const pageContent = (
    <div className={styles.pageContainer}>
      {/* Hero Section */}
      <GameHero game={game} />

      {/* Navigation Tabs */}
      <nav className={styles.sectionNav} aria-label={t.games.sectionNavLabel}>
        {sectionTabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => handleTabClick(id)}
            className={`${styles.navTabBtn} ${activeNavId === id ? styles.navTabBtnActive : ''}`}
            aria-current={activeNavId === id ? 'true' : undefined}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* Overview Section */}
      <section
        id="overview-section"
        className={styles.overviewSection}
        aria-label={t.games.tabs.overview}
      >
        <article className={styles.overviewCard}>
          <div className={styles.overviewHeader}>
            <h2 className={styles.overviewTitle}>{t.games.overview.aboutTitle}</h2>
          </div>
          <div className={styles.overviewBody}>
            {game.overview.split('\n\n').map((section, idx) => {
              const lines = section.split('\n');
              if (lines.length > 1) {
                const [title, ...descLines] = lines;
                return (
                  <div key={idx} className={styles.overviewSectionItem}>
                    <h3 className={styles.overviewSectionTitle}>{title}</h3>
                    <p className={styles.overviewParagraph}>{descLines.join('\n')}</p>
                  </div>
                );
              }
              return (
                <p key={idx} className={styles.overviewParagraph}>
                  {section}
                </p>
              );
            })}
          </div>
        </article>
      </section>

      {/* Features / Simulation Pillars Section */}
      <section
        id="features-section"
        className={styles.sectionBlock}
        aria-label={t.games.tabs.features}
      >
        <GamePillars pillars={game.pillars} vessels={game.vessels} />
      </section>

      {/* Media & Gallery Section */}
      <section id="media-section" className={styles.sectionBlock} aria-label={t.games.tabs.media}>
        <GameMediaGallery media={game.media} gameTitle={game.title} />
      </section>

      {/* Roadmap Section */}
      <section
        id="roadmap-section"
        className={styles.sectionBlock}
        aria-label={t.games.tabs.roadmap}
      >
        <GameRoadmap roadmap={game.roadmap} />
      </section>

      {/* Development Disclaimer & AI Disclosure Section (at the end of the page) */}
      <section className={styles.disclaimerCard} aria-labelledby="game-disclaimer-title">
        <div className={styles.disclaimerHeader}>
          <div className={styles.disclaimerIcon} aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>
          <h3 id="game-disclaimer-title" className={styles.disclaimerTitle}>
            {t.games.disclaimer.title}
          </h3>
        </div>
        <p className={styles.disclaimerText}>
          {game.disclaimer || t.games.disclaimer.aiDisclosureText({ title: game.title })}
        </p>
      </section>
    </div>
  );

  return isSailGame ? (
    <Suspense fallback={pageContent}>
      <OceanBackdrop className={styles.oceanPageBackdrop}>{pageContent}</OceanBackdrop>
    </Suspense>
  ) : (
    pageContent
  );
};
